import { isEqual } from 'lodash/fp';
import { FeatureFlag } from '@mews-commander/enums';
import { Amount } from '@mews-commander/types';
import { Responsive, SpacingSmallBottom } from '@mews-commander/ui';
import { useFeatureFlag, useScope } from '@mews/framework';
import { ExpandableBox } from '@optimus-web/helpers';
import { Container, Divider, ExpandableList, ExpandButton, Stack } from '@optimus-web/core';
import { stubNull, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { getBill, getBillOwnerId, getPaymentIdsByBillIds } from '@mews-commander/legacy-entities/src/ducks';
import { BillingGrouping, BillingMode } from '../../constants';
import { getBillingGrouping, isBillExpanded, SCROLL_TO_BILL_ANCHOR } from '../../ducks/billing';
import { PaymentCard } from '../PaymentCard';
import { isBillWithNoServiceOrders } from '../../ducks/billItems';
import { toggleExpandBill } from '../../actions/billing';
import { getBillClassification } from '../../ducks/bills';
import { BillControls } from './BillControls';
import { MonthList, ServiceList } from './ItemsGrouping';
import { PaymentList } from './PaymentList';
import { BillCardLayout, BillCardSidebarElement, MainContentElement } from './style';
import { PaymentRequestList } from './PaymentRequestList/PaymentRequestList';
import { BillHeader } from './BillHeader/BillHeader';

interface BillCardProps {
    amount?: Amount;
    billId: string;
    mode?: BillingMode;
    accountId: string;
}

export const BillCard = ({ amount, billId, mode, accountId }: BillCardProps) => {
    const dispatch = useCommanderThunkDispatch();
    const reservationLinkToPaymentsEnabled = useFeatureFlag(FeatureFlag.reservationLinkToPaymentsEnabled);

    const scope = useScope() ?? '';

    const currentEnterpriseId = useCurrentEnterpriseId();

    const billOwnerId = useCommanderSelector((state) => getBillOwnerId(state, { id: billId }));

    const paymentIds = useCommanderSelector(
        (state) => getPaymentIdsByBillIds(state, { billIds: [billId], enterpriseId: currentEnterpriseId }),
        isEqual
    );

    const bill = useCommanderSelector((state) => getBill(state, { id: billId }));

    const expanded = useCommanderSelector((state) => isBillExpanded(state, { billId, scope }));

    const grouping = useCommanderSelector((state) => getBillingGrouping(state, { scope }));

    const billClassification = getBillClassification({ billOwnerId: bill?.ownerId, accountId });

    const toggleExpanded = () => dispatch(toggleExpandBill({ billId, scope }));

    const TheExpandButton = () => <ExpandButton expanded={expanded} onClick={toggleExpanded} />;

    const isVirtual = bill?.isVirtual;
    const currentBillId = bill?.id || null;
    const hasNoServiceOrders = useCommanderSelector((state) =>
        isBillWithNoServiceOrders(state, { billId: currentBillId })
    );

    const cardLayoutProps = {
        [SCROLL_TO_BILL_ANCHOR]: billId,
        'data-test-unpaid-items-deposits': isVirtual ? 'true' : null,
    };

    return (
        <BillCardLayout {...cardLayoutProps} data-expanded={expanded}>
            <MainContentElement>
                <Stack>
                    <BillHeader
                        billId={billId}
                        mode={mode}
                        onClick={toggleExpanded}
                        billClassification={billClassification}
                        accountId={accountId}
                    />
                    <Responsive Default={stubNull} Phone={TheExpandButton} />
                </Stack>
                <ExpandableBox expanded={expanded}>
                    <Container padding={['0', '300']}>
                        {expanded ? (
                            <Stack vertical spacing="300">
                                <BillControls billId={billId} />
                                {!hasNoServiceOrders ? <Divider /> : null}
                                <Stack vertical>
                                    <ExpandableList multiSelect>
                                        {grouping === BillingGrouping.Month ? (
                                            <MonthList billId={billId} accountId={accountId} />
                                        ) : (
                                            <ServiceList billId={billId} accountId={accountId} />
                                        )}
                                        <PaymentList paymentIds={paymentIds} />
                                        {reservationLinkToPaymentsEnabled && accountId ? (
                                            <PaymentRequestList billId={billId} accountId={accountId} />
                                        ) : null}
                                    </ExpandableList>
                                </Stack>
                                <SpacingSmallBottom />
                            </Stack>
                        ) : null}
                    </Container>
                </ExpandableBox>
            </MainContentElement>
            {billOwnerId ? (
                <BillCardSidebarElement>
                    <PaymentCard
                        amount={amount}
                        accountId={billOwnerId}
                        billId={billId}
                        billClassification={billClassification}
                        expanded={expanded}
                        onClick={toggleExpanded}
                    />
                    <Responsive Default={TheExpandButton} Phone={stubNull} Tablet={stubNull} />
                </BillCardSidebarElement>
            ) : null}
        </BillCardLayout>
    );
};
